import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  FormFieldLabel,
  NativeSelect,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eureca/ui';
import type { DocumentKind } from './document-viewer-query';
import { SHARE_TTL_OPTIONS } from './document-viewer-query';
import { CopyIcon } from './icons';

export interface ShareDocumentDialogProps {
  kind: DocumentKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Validade selecionada (horas) — null = nada selecionado (SEM default, PFC-022). */
  ttlHours: number | null;
  onTtlChange: (ttlHours: number) => void;
  /** URL gerada — null antes do primeiro "Gerar link". */
  shareUrl: string | null;
  /** "Link disponível até dd/mm/aaaa, às hh:mm." — null antes de gerar. */
  shareExpiryLabel: string | null;
  isGenerating: boolean;
  onGenerateLink: () => void;
  onCopyLink: () => void;
}

const DIALOG_COPY: Record<DocumentKind, { title: string; description: string }> = {
  resume: {
    title: 'Compartilhar currículo',
    description:
      'Gera um link com prazo de validade, para enviar a clientes externos sem precisar de login. O link mostra apenas este currículo, protegido com marca d’água.',
  },
  attestation: {
    title: 'Compartilhar laudo',
    description:
      'Gera um link com prazo de validade, para enviar a clientes externos sem precisar de login. O link mostra apenas este laudo, protegido com marca d’água.',
  },
};

/**
 * Modal "Compartilhar currículo/laudo" (EU-594 PFC-022..027) — Figma
 * "Profile / Resume / Generate link" (15539:56650) e "Link generated"
 * (15539:56689). Puro: estado (validade/link) vive no container.
 *
 * - Antes de gerar: select SEM valor default + "Gerar link" desabilitado com
 *   tooltip "Configure a validade do link" (PFC-023).
 * - Depois de gerar: o select continua alterável (trocar re-gera o link no
 *   container — PFC-026), URL readonly com ícone de copiar, texto de validade
 *   e ações Voltar / Copiar link (PFC-027).
 */
export function ShareDocumentDialog({
  kind,
  open,
  onOpenChange,
  ttlHours,
  onTtlChange,
  shareUrl,
  shareExpiryLabel,
  isGenerating,
  onGenerateLink,
  onCopyLink,
}: ShareDocumentDialogProps) {
  const copy = DIALOG_COPY[kind];
  const hasLink = shareUrl !== null;
  const canGenerate = ttlHours !== null && !isGenerating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="document-share-dialog" className="gap-4 p-6">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <FormField>
            <FormFieldLabel className="sr-only">Validade do link</FormFieldLabel>
            <NativeSelect
              value={ttlHours === null ? '' : String(ttlHours)}
              variant={ttlHours === null ? 'placeholder' : 'default'}
              disabled={isGenerating}
              onChange={(e) => {
                const next = Number(e.target.value);
                if (Number.isFinite(next) && next > 0) onTtlChange(next);
              }}
              data-testid="document-share-ttl"
              aria-label="Validade do link"
            >
              <option value="" disabled>
                Validade do link
              </option>
              {SHARE_TTL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </NativeSelect>
          </FormField>

          {hasLink && (
            <>
              <div className="relative">
                <input
                  readOnly
                  value={shareUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm text-foreground shadow-2xs outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  data-testid="document-share-url"
                  aria-label="Link de compartilhamento"
                />
                <button
                  type="button"
                  onClick={onCopyLink}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  aria-label="Copiar link"
                  data-testid="document-share-copy-icon"
                >
                  <CopyIcon className="size-4" />
                </button>
              </div>
              {shareExpiryLabel && (
                <p className="text-sm text-muted-foreground" data-testid="document-share-expiry">
                  {shareExpiryLabel}
                </p>
              )}
            </>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="document-share-close"
          >
            Voltar
          </Button>
          {hasLink ? (
            <Button
              type="button"
              onClick={onCopyLink}
              disabled={isGenerating}
              data-testid="document-share-copy"
            >
              Copiar link
            </Button>
          ) : (
            <GenerateLinkButton
              canGenerate={canGenerate}
              isGenerating={isGenerating}
              onGenerateLink={onGenerateLink}
            />
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Gerar link" desabilitado precisa de tooltip no HOVER (PFC-023) — botão
 * `disabled` não dispara mouse events em todos os engines, então o trigger é
 * um wrapper que derruba os pointer-events do filho (mesma receita do
 * BlockedEditTooltip do EU-569/570).
 */
function GenerateLinkButton({
  canGenerate,
  isGenerating,
  onGenerateLink,
}: {
  canGenerate: boolean;
  isGenerating: boolean;
  onGenerateLink: () => void;
}) {
  const button = (
    <Button
      type="button"
      onClick={onGenerateLink}
      disabled={!canGenerate}
      data-testid="document-share-generate"
    >
      {isGenerating ? 'Gerando…' : 'Gerar link'}
    </Button>
  );

  if (canGenerate || isGenerating) return button;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="cursor-not-allowed [&>*]:pointer-events-none"
            data-testid="document-share-generate-blocked"
          >
            {button}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">Configure a validade do link</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
